import {
  MenuGroup,
  MenuGroupHeader,
  MenuItemCheckbox,
} from "@fluentui/react-components";
import * as React from "react";
import { ConditionalWrap } from "../../ConditionalWrap/ConditionalWrap";
import type { Appearance } from "../../data";
import { useStyles } from "../PromptFilter.styles";
import { FILTER_CONCATENATE_TOKEN, type FilterOption } from "../types";

export type RenderMenuOptionsProps = {
  groupKey: string;
  groupLabel: string;
  filterOptions: FilterOption[];
  selectedValues: Record<string, string[]>;
  valueToLabel: (value: string) => string;
  showGroupHeader?: boolean;
  appearance?: Appearance;
};

/**
 * Render a list of options under a combobox. If groupKey is not "NA", render a grouped options otherwise regular options.
 * @param groupKey group key of the options. By default it would "NA" which means the list of options doesn't belong to any group.
 * @param groupLabel group label.
 * @param filterOptions list of filter options.
 * @param selectedValues list of selected values, used to set correct aria-checked on each option.
 * @param valueToLabel function that converts option's value to a proper label.
 * @returns a list of options under a combobox.
 */
export const RenderMenuOptions = ({
  groupKey,
  groupLabel,
  filterOptions,
  selectedValues,
  valueToLabel,
  showGroupHeader = true,
  appearance,
}: RenderMenuOptionsProps) => {
  const styles = useStyles(appearance)();
  const options = filterOptions.map((x: FilterOption) => (
    <MenuItemCheckbox
      name={x.value}
      value={
        x.value +
        (groupKey !== "NA" ? `${FILTER_CONCATENATE_TOKEN}${groupKey}` : "")
      }
      key={x.value}
      aria-label={`${x.displayLabel || valueToLabel(x.value)}-menuitemcheckbox`}
      aria-checked={selectedValues[groupKey]?.includes(x.value)}
      disabled={x.disabled}
      className={x.badge ? "" : styles.menuItemLineWrap}
    >
      <ConditionalWrap
        condition={!!x.badge}
        wrapIfTrue={(children: React.ReactNode) => (
          <div className={styles.optionContainer} key={x.value}>
            {children}
          </div>
        )}
      >
        {!!x.badge && <div className={styles.optionBadge}>{x.badge}</div>}
        {x.displayLabel || valueToLabel(x.value)}
      </ConditionalWrap>
    </MenuItemCheckbox>
  ));

  if (groupKey === "NA") {
    return <>{options}</>;
  } else {
    return (
      <MenuGroup data-testid="option-group">
        {showGroupHeader && <MenuGroupHeader>{groupLabel}</MenuGroupHeader>}
        {options}
      </MenuGroup>
    );
  }
};
